import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import { DELETE_TAX_GROUP, GET_ALL_TAX_GROUPS } from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";

export interface ICStateOState {
  id: string;
  type: string;
  name: string;
  rate: number;
}

export interface ITaxGroups {
  id: string;
  name: string;
  total_company_state_rate: number;
  total_other_state_rate: number;
  tax_component: [ICStateOState];
}

export function getTaxGroupsFields<T>(
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("name"),
      key: "name",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("company_state"),
      key: "tax_component",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-4",
      format: ({ field, data }: ITabelFieldViewProps<T>) => {
        const filteredData = data["tax_component"]
          .filter((item: any) => item.type === "company_state")
          .map((item: any) => `${item.name}: ${item.rate}%`)
          .join("  |  ");

        return filteredData || "-";
      },
    },
    {
      name: t("other_state"),
      key: "tax_component",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-4",
      format: ({ field, data }: ITabelFieldViewProps<T>) => {
        const filteredData = data["tax_component"]
          .filter((item: any) => item.type === "other_state")
          .map((item: any) => `${item.name}: ${item.rate}%`)
          .join("  |  ");
        return filteredData || "-";
      },
    },
    {
      name: t("total_company_state_rate"),
      key: "total_company_state_rate",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-1",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data["tax_component"].filter(
          (item: any) => item.type === "company_state"
        ).length === 0
          ? "-"
          : data["total_company_state_rate"],
      assistiveText: t("total_company_state_rate_title"),
    },
    {
      name: t("total_other_state_rate"),
      key: "total_other_state_rate",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-1",
      format: ({ field, data }: ITabelFieldViewProps<T>) =>
        data["tax_component"].filter((item: any) => item.type === "other_state")
          .length === 0
          ? "-"
          : data["total_other_state_rate"],
      assistiveText: t("total_other_state_rate_title"),
    },

    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,

      flex: "col-span-2",
      component: actionComponent,
    },
  ];
}

export const getAllTaxGroups = async (
  setTaxGroups: TReactSetState<ITaxGroups[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string
) => {
  try {
    const taxGroupsField = await GET_ALL_TAX_GROUPS(pagination, search);
    if (taxGroupsField && taxGroupsField.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setTaxGroups(taxGroupsField.data.items);
      setPagination(taxGroupsField.data.pagination);
    } else {
      setTaxGroups([]);
      sweetAlertError({
        message: taxGroupsField.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    handleError(error, "getAllTaxGroups");
  }
};

export const deleteTaxGroupField = async (id: string) => {
  try {
    const deletedData = await DELETE_TAX_GROUP(id);
    if (deletedData && deletedData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deletedData.message });
      return true;
    } else {
      sweetAlertError({
        message: deletedData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    handleError(e, "deleteTaxGroupField");
    return false;
  }
};
